import { type ReactiveVar, useReactiveVar } from "@apollo/client";

import { makeFSVar } from "@shared/reactiveVar/makeFSVar";
import type { Taxonomy } from "@shared/types/graphqlGenerated";

type CatalogSearchResultTitle = string | Taxonomy;
type CatalogSearchResultType = "taxonomy" | "ingredient" | "brand" | "";

interface CatalogSearchResultHeader {
  title: CatalogSearchResultTitle;
  type: CatalogSearchResultType;
}

const INITIAL_STATE: CatalogSearchResultHeader = {
  title: "",
  type: "",
};

const catalogSearchResultTitle: ReactiveVar<CatalogSearchResultHeader> = makeFSVar(
  INITIAL_STATE,
  "useCatalogSearchResultTitle"
);

const resetCatalogSearchResultTitle = () => {
  catalogSearchResultTitle(INITIAL_STATE);
};

const setIngredientCatalogSearchResultTitle = (ingredientName: string) => {
  catalogSearchResultTitle({
    title: ingredientName,
    type: "ingredient",
  });
};

const setBrandCatalogSearchResultTitle = (brandName: string) => {
  catalogSearchResultTitle({ title: brandName, type: "brand" });
};

const setOptimizeMegaMenuCatalogSearchResultTitle = (
  taxonomy: Taxonomy,
  parentTaxonomy?: Taxonomy
): void => {
  const storedCatalogSearchResultTitle = catalogSearchResultTitle();
  const taxonomyHasChildren: boolean = (taxonomy?.children?.length ?? 0) > 0;

  if (storedCatalogSearchResultTitle.type === "") {
    if (taxonomyHasChildren) {
      catalogSearchResultTitle({
        title: taxonomy,
        type: "taxonomy",
      });

      return;
    }

    if (parentTaxonomy) {
      catalogSearchResultTitle({
        title: parentTaxonomy,
        type: "taxonomy",
      });
    }

    return;
  }

  if (storedCatalogSearchResultTitle.type === "taxonomy") {
    if (taxonomy.id === (storedCatalogSearchResultTitle.title as Taxonomy).id) {
      return;
    }

    if (taxonomy.depth > (storedCatalogSearchResultTitle.title as Taxonomy).depth) {
      if (taxonomyHasChildren) {
        catalogSearchResultTitle({
          title: taxonomy,
          type: "taxonomy",
        });

        return;
      }

      if (!taxonomyHasChildren && parentTaxonomy) {
        catalogSearchResultTitle({
          title: parentTaxonomy,
          type: "taxonomy",
        });

        return;
      }
    }

    if (taxonomy.depth === (storedCatalogSearchResultTitle.title as Taxonomy).depth) {
      if (parentTaxonomy) {
        catalogSearchResultTitle({
          title: parentTaxonomy,
          type: "taxonomy",
        });
      }
    }
  }
};

const useCatalogSearchResultTitle = (): CatalogSearchResultHeader => {
  return useReactiveVar(catalogSearchResultTitle);
};

export {
  resetCatalogSearchResultTitle,
  setOptimizeMegaMenuCatalogSearchResultTitle,
  setIngredientCatalogSearchResultTitle,
  setBrandCatalogSearchResultTitle,
  useCatalogSearchResultTitle,
  catalogSearchResultTitle,
};
